import Stack from '@mui/material/Stack'
import useMediaQuery from '@mui/material/useMediaQuery'
import { MonitoredRoute } from 'App'
import theme from 'assets/theme'
import { getStartPage } from 'context/authentication/User.helpers'
import { CarriersViewProvider } from 'context/CarriersViewContext'
import { CompaniesViewProvider } from 'context/CompaniesViewContext'
import { DriversViewProvider } from 'context/DriversViewContext'
import { useMenuContext } from 'context/menu/MenuContext'
import { NavigationContextProvider } from 'context/navigation/NavigationContext'
import { QuotesViewProvider } from 'context/QuotesViewContext'
import { RejectedMessagesViewProvider } from 'context/RejectedMessagesViewContext'
import { TripDetailsProvider } from 'context/trips/TripDetailsContext'
import { TripsViewProvider } from 'context/TripsViewContext'
import { UsersViewProvider } from 'context/UsersViewContext'
import { VehiclesViewProvider } from 'context/VehiclesViewContext'
import DrawerMenu from 'modules/DrawerMenu/DrawerMenu'
import Footer from 'modules/Footer/Footer'
import Support from 'modules/Support/Support'
import TopNavigation from 'modules/TopNavigation/TopNavigation'
import { type ReactElement } from 'react'
import { Redirect, Switch } from 'react-router-dom'
import CarrierManager from 'screens/CarrierManager/CarrierManager'
import CarrierProfilePage from 'screens/CarrierProfile/CarrierProfile'
import CompanyManager from 'screens/CompanyManager/CompanyManager'
import DriverManager from 'screens/DriverManager/DriverManager'
import FaqPage from 'screens/Faq/FaqPage'
import AdminRoute from 'screens/Home/AdminRoute'
import ProfilePage from 'screens/ProfilePage/ProfilePage'
import QuoteDetails from 'screens/QuoteDetails/QuoteDetails'
import Quotes from 'screens/Quotes/Quotes'
import RejectedMessages from 'screens/RejectedMessages/RejectedMessages'
import TripDashboard from 'screens/TripDashboard/TripDashboard'
import TripDetails from 'screens/TripDetails/TripDetails'
import UserManager from 'screens/UserManager/UserManager'
import VehicleManager from 'screens/VehicleManager/VehicleManager'

const Home = (): ReactElement => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const startPage = getStartPage()

  // Drawer Menu
  const [menuState] = useMenuContext()
  const { menuExtended } = menuState.menuContext

  const setMarginLeft = (): string => {
    return menuExtended ? '14rem' : '6rem'
  }

  return (
    <NavigationContextProvider>
      <TopNavigation />
      <DrawerMenu />
      <Stack
        direction="column"
        justifyContent="space-between"
        spacing={2}
        sx={{
          paddingTop: '4rem',
          paddingBottom: '1rem',
          transition: 'margin-left 0.3s',
          minHeight: '100vh',
          marginLeft: isMobile ? 0 : setMarginLeft(),
        }}
      >
        <Switch>
          <MonitoredRoute exact path="/">
            <Redirect to={startPage} />
          </MonitoredRoute>
          <MonitoredRoute path="/trips">
            <TripsViewProvider>
              <Switch>
                <MonitoredRoute exact path="/trips">
                  <TripDashboard />
                  <Support />
                </MonitoredRoute>
                <MonitoredRoute exact path="/trips/:tripCid">
                  <TripDetailsProvider>
                    <TripDetails />
                  </TripDetailsProvider>
                  <Support />
                </MonitoredRoute>
              </Switch>
            </TripsViewProvider>
          </MonitoredRoute>
          <MonitoredRoute exact path="/drivers">
            <DriversViewProvider>
              <DriverManager />
            </DriversViewProvider>
            <Support />
          </MonitoredRoute>
          <MonitoredRoute exact path="/vehicles">
            <VehiclesViewProvider>
              <VehicleManager />
            </VehiclesViewProvider>
            <Support />
          </MonitoredRoute>
          <MonitoredRoute path="/quotes">
            <QuotesViewProvider>
              <Switch>
                <MonitoredRoute exact path="/quotes">
                  <Quotes />
                  <Support />
                </MonitoredRoute>
                <MonitoredRoute exact path="/quotes/:token">
                  <QuoteDetails />
                  <Support />
                </MonitoredRoute>
              </Switch>
            </QuotesViewProvider>
          </MonitoredRoute>
          <MonitoredRoute exact path="/carrier-profile">
            <CarrierProfilePage />
          </MonitoredRoute>
          <MonitoredRoute exact path="/profile">
            <ProfilePage />
          </MonitoredRoute>
          <MonitoredRoute path="/help-and-info">
            <FaqPage />
          </MonitoredRoute>
          <AdminRoute exact path="/users">
            <UsersViewProvider>
              <UserManager />
            </UsersViewProvider>
          </AdminRoute>
          <AdminRoute exact path="/companies" operatorOnly>
            <CompaniesViewProvider>
              <CompanyManager />
            </CompaniesViewProvider>
          </AdminRoute>
          <AdminRoute exact path="/carriers" operatorOnly>
            <CarriersViewProvider>
              <CarrierManager />
            </CarriersViewProvider>
          </AdminRoute>
          <AdminRoute exact path="/rejected-messages" operatorOnly>
            <RejectedMessagesViewProvider>
              <RejectedMessages />
            </RejectedMessagesViewProvider>
          </AdminRoute>
        </Switch>
        <Footer sx={{ paddingX: 3 }} />
      </Stack>
    </NavigationContextProvider>
  )
}

export default Home
